import React from 'react';
import SlideLayout, { SlideTitle, SlideContent } from '../components/SlideLayout';
import { DollarSign, Cpu, Zap } from 'lucide-react';

const MoESlide = () => {
  return (
    <SlideLayout maxWidth="6xl">
      <SlideTitle 
        subtitle="Sparse activation enables massive scale with reasonable compute"
      >
        Mixture of Experts (MoE)
      </SlideTitle>
      
      <SlideContent>
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Why MoE */}
          <div>
            <h3 className="font-heading text-xl font-semibold text-foreground mb-6">Why MoE Matters</h3>
            
            <div className="space-y-4">
              <div className="slide-card">
                <div className="flex items-center gap-3 mb-2">
                  <Cpu className="w-5 h-5 text-primary" />
                  <span className="font-heading font-semibold text-foreground">DeepSeek V3</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  <span className="text-primary font-mono text-lg">671B</span> parameters total, only 
                  <span className="text-accent font-mono text-lg"> 37B</span> active per token
                </p>
              </div>
              
              <div className="slide-card">
                <div className="flex items-center gap-3 mb-2">
                  <DollarSign className="w-5 h-5 text-accent" />
                  <span className="font-heading font-semibold text-foreground">Cost Reduction</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Training cost: <span className="text-accent font-mono">~$5M</span> vs 
                  <span className="text-destructive font-mono"> $50-500M</span> for dense models
                </p>
              </div>
              
              <div className="slide-card">
                <div className="flex items-center gap-3 mb-2">
                  <Zap className="w-5 h-5 text-chart-4" />
                  <span className="font-heading font-semibold text-foreground">Deployment</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Critical for resource-constrained environments like NOC systems
                </p>
              </div>
            </div>
          </div>
          
          {/* Visual representation */}
          <div className="slide-card">
            <h4 className="font-heading text-lg font-semibold text-foreground mb-6 text-center">MoE Routing</h4>
            
            {/* Router visualization */}
            <div className="relative">
              {/* Input */}
              <div className="flex justify-center mb-4">
                <div className="px-4 py-2 rounded-lg bg-primary/20 border border-primary/30 text-sm text-foreground">
                  Input Token
                </div>
              </div>
              
              {/* Arrow */}
              <div className="flex justify-center mb-4">
                <div className="w-0.5 h-8 bg-gradient-to-b from-primary to-muted" />
              </div>
              
              {/* Router */}
              <div className="flex justify-center mb-4">
                <div className="px-6 py-3 rounded-xl bg-secondary/20 border border-secondary/30">
                  <span className="text-sm font-medium text-foreground">Router Network</span>
                  <p className="text-xs text-muted-foreground mt-1">Selects top-k experts</p>
                </div>
              </div>
              
              {/* Arrows to experts */}
              <div className="flex justify-center gap-8 mb-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className={`w-0.5 h-6 ${i <= 2 ? 'bg-accent' : 'bg-muted/30'}`} />
                ))}
              </div>
              
              {/* Experts */}
              <div className="grid grid-cols-4 gap-2">
                {[1, 2, 3, 4].map((i) => (
                  <div 
                    key={i}
                    className={`p-3 rounded-lg text-center text-xs ${
                      i <= 2 
                        ? 'bg-accent/20 border border-accent/30 text-foreground' 
                        : 'bg-muted/20 border border-muted/30 text-muted-foreground'
                    }`}
                  >
                    Expert {i}
                    {i <= 2 && <div className="text-accent mt-1">Active</div>}
                  </div>
                ))}
              </div>
              
              {/* Legend */}
              <div className="flex justify-center gap-4 mt-6 text-xs">
                <span className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-accent" /> Selected (top-k=2)
                </span>
                <span className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-muted/30" /> Inactive
                </span>
              </div>
            </div>
          </div>
        </div>
      </SlideContent>
    </SlideLayout>
  );
};

export default MoESlide;
